import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatErr } from "@/lib/api";
import Logo from "@/components/Logo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), password);
      nav("/dashboard");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-5">
      {/* Side panel */}
      <div className="hidden lg:flex lg:col-span-2 bg-[--sky] text-[--ink] p-10 flex-col justify-between border-r border-[--line]">
        <Link to="/" data-testid="brand-link" className="flex items-center">
          <Logo className="h-9" />
        </Link>
        <div>
          <div className="overline text-[--accent] mb-4">AUDIO → SRT → TRANSLATE</div>
          <h2 className="font-display font-black text-4xl tracking-tighter leading-[0.95]">
            Welcome back.
            <br />
            Subtitle at speed.
          </h2>
          <p className="mt-4 text-[--ink]/70 max-w-sm leading-relaxed">
            Speech customized for your business. Pick up where you left off.
          </p>
        </div>
        <div className="font-mono text-xs text-[--muted]">Flowscribe-WEngine · Flowscribe-GPEngine · Flowscribe-EEngine · Flowscribe-GEngine · Flowscribe-AEngine</div>
      </div>

      {/* Form */}
      <div className="lg:col-span-3 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center mb-10">
            <Logo className="h-8" />
          </Link>
          <div className="overline text-[--muted] mb-4">SIGN&nbsp;IN</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mb-8">Enter the studio.</h1>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="overline block mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
                placeholder="you@studio.com"
              />
            </div>
            <div>
              <label className="overline block mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="w-full bg-[--accent] text-white py-3 text-sm font-bold hover:bg-[--accent-hi] transition-colors disabled:opacity-50"
            >
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <div className="text-right">
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                data-testid="login-forgot-password-link"
                className="text-xs font-mono text-[--muted] hover:text-[--ink] underline underline-offset-4 decoration-dotted"
              >
                Forgot password?
              </button>
            </div>
          </form>

          <p className="mt-6 text-xs text-[--muted] font-mono leading-relaxed">
            Accounts are created by your administrator. Contact your admin if you need access.
          </p>
        </div>
      </div>

      {showForgot && (
        <ForgotPasswordModal
          defaultEmail={email}
          onClose={() => setShowForgot(false)}
        />
      )}
    </div>
  );
}

function ForgotPasswordModal({ defaultEmail = "", onClose }) {
  const [email, setEmail] = useState(defaultEmail);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: trimmed });
      setSent(true);
    } catch (err) {
      // Endpoint is enumeration-safe → on error fall back to the same UX so
      // we don't accidentally reveal whether the email exists.
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-[--ink]/60 backdrop-blur-sm grid place-items-center z-50 p-4"
      onClick={onClose}
      data-testid="forgot-password-modal"
    >
      <div
        className="bg-white hard-border max-w-md w-full p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="overline text-[--muted] mb-2">PASSWORD RESET</div>
        {sent ? (
          <>
            <h2 className="font-display font-black text-2xl tracking-tighter">Check your inbox.</h2>
            <p className="text-sm text-[--muted] mt-3 leading-relaxed">
              If an account exists for <span className="font-mono text-[--ink]">{email}</span>, we've
              sent a reset link that's valid for 30 minutes. Don't see it? Check your spam folder.
            </p>
            <div className="flex justify-end mt-6">
              <button
                onClick={onClose}
                data-testid="forgot-password-close-btn"
                className="bg-[--accent] text-white px-5 py-2.5 text-xs font-bold hover:bg-[--accent-hi]"
              >
                Got it
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <h2 className="font-display font-black text-2xl tracking-tighter">Forgot your password?</h2>
            <p className="text-sm text-[--muted] mt-3 leading-relaxed">
              Enter the email you sign in with. We'll send you a one-time link to set a new password.
            </p>
            <label className="overline block mt-5 mb-2">Email</label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="forgot-password-email-input"
              className="w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
              placeholder="you@studio.com"
            />
            <div className="flex justify-end gap-2 mt-6">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                data-testid="forgot-password-cancel-btn"
                className="border border-[--ink] px-4 py-2.5 text-xs font-bold hover:bg-[--paper] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy || !email.trim()}
                data-testid="forgot-password-submit-btn"
                className="bg-[--accent] text-white px-5 py-2.5 text-xs font-bold hover:bg-[--accent-hi] disabled:opacity-50"
              >
                {busy ? "Sending…" : "Send reset link"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
